import ApiClient from '@/utils/apiClient'

import type { AxiosResponse } from 'axios'
import type { PortalApiResponse } from '@/types/apiResponses'
import type { Portal } from '@/types/portal'
import { useAppStore } from '@/stores/app'
import { useAppCookies } from '@/composables/useCookies'
import { loginDomainMappings } from '@/constants/appDomainMappings'
import type { BusinessProfile } from '@/types/businessProfile'

class AppService {
  apiClient: any
  constructor() {
    this.apiClient = new ApiClient(import.meta.env.VITE_DX_API_URL)
  }

  getPortalData = async (portalDomain: string): Promise<Portal> => {
    const portalData: PortalApiResponse = await this.apiClient.get(
      'portals',
      { domain: portalDomain },
      { headers: { 'X-Bypass-Login': true } },
    )

    return portalData.data[0]
  }

  getUserData = async (emailAddress: string) => {
    const response = await this.apiClient.get('users', {
      email: emailAddress,
    })

    return response
  }

  getUserById = async (userId: string) => {
    const response = await this.apiClient.get('users', {
      ids: userId,
    })

    return response
  }

  getPortalBusinessProfiles = async (
    userId: number | undefined,
    portalId: number | undefined,
  ): Promise<BusinessProfile[]> => {
    const response: AxiosResponse = await this.apiClient.get(
      `portals/${portalId}/users/${userId}/businessProfiles`,
      {
        userId: userId,
      },
    )
    let businessProfiles = response.data
    // Get unique profiles (There will be duplicates when a portal admin is accessing it)
    businessProfiles = [
      ...new Map(businessProfiles.map((bp: BusinessProfile) => [bp.id, bp])).values(),
    ]
    return businessProfiles
  }

  loginWithEmbedToken = async (
    clientToken: string,
    userId: string,
    brandId: string | undefined,
  ) => {
    const response = await this.apiClient.apiMethods.loginWithEmbedToken(
      clientToken,
      userId,
      brandId,
    )
    return response
  }

  assignDivisionBrandsToClient = async (
    businessProfileId: number | null,
    brandIds: string[],
    roleId: number,
  ) => {
    const requestData = {
      name: 'Stunnr BE - Client Division',
      brandIds,
      roleId,
      updateUserSettings: true,
    }

    const response = await this.apiClient.post(
      `businessProfiles/${businessProfileId}/divisions`,
      requestData,
    )

    return response
  }

  submitCretiveFeedback = async (
    businessProfileId: number | null,
    reaction: number,
    comment: string | null,
    imageUrl: string,
  ): Promise<number> => {
    const requestData = {
      reaction,
      comment,
      type: 1,
      appId: 12,
      imageUrl,
    }
    const response = await this.apiClient.post(
      `businessProfiles/${businessProfileId}/generatedCreativeFeedbacks`,
      requestData,
    )
    return response.id
  }

  updateCreativeFeedback = async (
    businessProfileId: number | null,
    feedbackId: number,
    reaction: number,
    comment: string | null,
  ): Promise<void> => {
    await this.apiClient.patch(
      `businessProfiles/${businessProfileId}/generatedCreativeFeedbacks/${feedbackId}`,
      { reaction, comment },
    )
  }

  clearAuthData = async (clearSessionInRedis: boolean = true) => {
    const appStore = useAppStore()

    if (clearSessionInRedis) {
      await this.apiClient.apiMethods.logout()
    }

    this.removeUserTokens()

    if (!import.meta.env.DEV) {
      const redirectUrl = window.location.hostname
      window.location.href = `https://${loginDomainMappings[appStore.getPortalDomain as string] + '.' + redirectUrl}`
    } else {
      throw new Error('Missing auth cookies')
    }
  }

  removeUserTokens = () => {
    const { removeCookies } = useAppCookies()

    localStorage.removeItem('accessToken')
    removeCookies('user')
    removeCookies('accessToken')
    removeCookies('accessToken', '/', window.location.hostname)
  }
}

export default AppService
